import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {Check, Copy, X} from 'lucide-react';
import {PhoneHQButton} from 'ch3-ui-lib';

interface CodeBlockViewerProps {
    text: string;
    onClose: () => void;
}

export const CodeBlockViewer: React.FC<CodeBlockViewerProps> = ({text, onClose}) => {
    const intl = useIntl();
    const [copied, setCopied] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (e.key !== 'Tab') return;

            const container = containerRef.current;
            if (!container) return;

            const focusable = Array.from(
                container.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
            ).filter(el => !el.hasAttribute('disabled'));
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const closeButton = containerRef.current?.querySelector<HTMLElement>('[data-testid="chat.code-block.viewer-close"]');
        closeButton?.focus();

        return () => {
            previouslyFocused?.focus?.();
        };
    }, []);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopied(false);
        }
    };

    return (
        <div
            ref={containerRef}
            className="fixed inset-0 flex flex-col z-50 backdrop-blur-sm"
            style={{backgroundColor: 'rgba(0, 0, 0, 0.84)'}}
            data-testid="chat.code-block.viewer"
            role="dialog"
            aria-modal="true"
            aria-label={intl.formatMessage({id: 'chat.code-block.viewer-label', defaultMessage: 'Code block'})}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="absolute m-1 top-0 left-0 right-0 p-4 flex items-center justify-end gap-2 z-10">
                <PhoneHQButton
                    variant="secondary"
                    size="icon"
                    onClick={handleCopy}
                    className="!w-9 !h-9 flex items-center justify-center"
                    aria-label={intl.formatMessage(
                        copied
                            ? {id: 'chat.code-block.copied', defaultMessage: 'Copied'}
                            : {id: 'chat.code-block.copy', defaultMessage: 'Copy'}
                    )}
                >
                    {copied ? <Check size={16}/> : <Copy size={16}/>}
                </PhoneHQButton>
                <PhoneHQButton
                    variant="secondary"
                    size="icon"
                    onClick={onClose}
                    className="!w-9 !h-9 flex items-center justify-center"
                    aria-label={intl.formatMessage({id: 'chat.code-block.close', defaultMessage: 'Close'})}
                    data-testid="chat.code-block.viewer-close"
                >
                    <X size={20}/>
                </PhoneHQButton>
            </div>
            <div
                className="flex-1 overflow-auto p-4 pt-20"
                onClick={(e) => {
                    if (e.target === e.currentTarget) onClose();
                }}
            >
                <pre className="font-mono text-sm text-white whitespace-pre m-0 w-fit min-w-full">{text}</pre>
            </div>
        </div>
    );
};
